import { Changeset } from 'ember-changeset';
import { BufferedChangeset } from 'ember-changeset/types';
import { v4 as uuid } from 'uuid';
import { WidgetsRegistry } from 'volta/components/dashboard';
import { IDashboardWidgetWithComponent, TDashboardColumn } from 'volta/models/types/user';
import AuthorizationService from 'volta/services/authorization';
import StoreService from 'volta/services/store';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  widgets: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>;
  onSubmit: (widget: IDashboardWidgetWithComponent) => void;
  display: TDashboardColumn;
}

export default class EditBlockForm extends Component<IArgs> {
  @service store!: StoreService;
  @service authorization!: AuthorizationService;

  @tracked newWidget?: IDashboardWidgetWithComponent;
  @tracked widgetArgs: Record<string, unknown> = {};
  @tracked argChangeset!: BufferedChangeset;

  registry = WidgetsRegistry(this.authorization);

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset();
  }

  get widgetOptions() {
    const { widgets } = this.args;
    return Object.entries(this.registry)
      .map(([name, widget]) => {
        const columns: TDashboardColumn[] = [];
        if (widgets.primary.find((w) => w.name === name)) {
          columns.push('primary');
        }
        if (widgets.secondary.find((w) => w.name === name)) {
          columns.push('secondary');
        }
        if (widget.columnsDisplay.includes(this.args.display)) {
          return {
            ...widget,
            name,
            disabled: false,
            column: widget.columnsDisplay[0],
            _id: uuid(),
            columns
          };
        }
        return undefined;
      })
      .filter(Boolean);
  }

  get selectedWidget() {
    if (!this.newWidget) {
      return false;
    }
    return this.widgetOptions.find((c) => c!.name === this.newWidget!.name);
  }

  @action
  selectWidget(widget: IDashboardWidgetWithComponent) {
    this.newWidget = { ...widget, _id: uuid() };
    this.initChangeset();
  }

  @action
  submitNewWidget() {
    if (!this.newWidget) {
      return;
    }
    if (this.argChangeset.isPristine) {
      this.args.onSubmit(this.newWidget);
    } else {
      this.argChangeset.validate();

      if (this.argChangeset.isValid) {
        this.argChangeset.execute();
        try {
          this.args.onSubmit({ ...this.newWidget, args: this.widgetArgs });
          this.initChangeset();
          this.newWidget = undefined;
        } catch (e) {
          defaultErrorHandler(e);
        }
      }
    }
  }

  @action
  initChangeset() {
    this.argChangeset = Changeset(this.widgetArgs);
  }
}
